/**
 * La campana de novedades, en la barra superior.
 *
 * Lo que llega acá es todo lo que le pasa a una orden: la emisión, cada cambio
 * de estado, el avance de sus líneas, la confirmación parcial de un
 * distribuidor, la propuesta de ajuste del equipo y el visto bueno o el rechazo
 * del cliente. Las tres cuentas la tienen y cada una ve lo suyo, porque el
 * servidor filtra con el mismo alcance que rige el resto de la API.
 *
 * ── Dos cosas que decide el SERVIDOR y no esta pantalla ────────────────────
 *
 *  · EL TÍTULO. Se compone allá desde la transición de estado, no del texto
 *    libre del evento. Es lo que permite mostrar la misma fila a los tres
 *    alcances sin revisar qué dice.
 *  · EL DESTINO. Cada notificación trae su `enlace`: un cliente y el equipo van
 *    a la ficha de la orden, un proveedor a su lista de órdenes recibidas —la
 *    ficha es la vista del comprador y le hablaría de faenas y totales de
 *    venta—. Calcularlo acá habría significado repetir esa regla de rol en la
 *    interfaz, donde puede quedar desfasada de la del servidor.
 *
 * ── Por qué se marca todo como leído al ABRIR ──────────────────────────────
 *
 * Porque abrir la campana ES leerla. Exigir además un botón «marcar como
 * leídas» deja el globo encendido sobre novedades que la persona ya miró, y a
 * los dos días el número deja de significar algo y se ignora.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMarcarNotificacionesLeidas, useNotificaciones } from '../api/hooks'
import { IconoCampana } from './icons'
import { fecha } from '../lib/format'

/**
 * Cuánto hace, en palabras.
 *
 * En una campana la hora exacta no sirve: lo que se decide con ella es si algo
 * es de recién o de ayer. Pasado un día se muestra la fecha, que a esa distancia
 * vuelve a ser el dato útil.
 */
function hace(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutos < 1) return 'recién'
  if (minutos < 60) return `hace ${minutos} min`
  const horas = Math.floor(minutos / 60)
  if (horas < 24) return `hace ${horas} h`
  return fecha(iso)
}

export function Campana() {
  const [abierta, setAbierta] = useState(false)
  const caja = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const consulta = useNotificaciones()
  const marcar = useMarcarNotificacionesLeidas()

  const datos = consulta.data?.datos ?? []
  const noLeidas = consulta.data?.noLeidas ?? 0
  const saturado = consulta.data?.saturado ?? false

  /*
   * Un clic fuera cierra el panel. Sin esto queda abierto sobre la página
   * después de que la persona ya se fue a otra cosa, tapando lo de abajo.
   */
  useEffect(() => {
    if (!abierta) return
    function alClic(ev: MouseEvent) {
      if (caja.current && !caja.current.contains(ev.target as Node)) setAbierta(false)
    }
    function alEscape(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setAbierta(false)
    }
    document.addEventListener('mousedown', alClic)
    document.addEventListener('keydown', alEscape)
    return () => {
      document.removeEventListener('mousedown', alClic)
      document.removeEventListener('keydown', alEscape)
    }
  }, [abierta])

  function alternar() {
    const siguiente = !abierta
    setAbierta(siguiente)
    // Abrir es leer. Se marca una sola vez y solo si había algo pendiente.
    if (siguiente && noLeidas > 0 && !marcar.isPending) marcar.mutate()
  }

  function ir(enlace: string) {
    setAbierta(false)
    navigate(enlace)
  }

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={alternar}
        aria-expanded={abierta}
        aria-label={
          noLeidas > 0
            ? `Novedades: ${saturado ? 'más de ' : ''}${noLeidas} sin leer`
            : 'Novedades'
        }
        className="relative inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5
          text-acero-300 hover:bg-acero-800 hover:text-white"
      >
        <IconoCampana size={17} />
        {/*
          El globo solo existe cuando hay algo. Un cero permanente junto al icono
          es ruido: ocupa el lugar de una señal y no señala nada.
        */}
        {noLeidas > 0 && (
          <span
            className="num absolute -right-0.5 -top-0.5 rounded-sm bg-senal-500 px-1
              text-[10px] font-semibold leading-[15px] text-acero-950"
          >
            {saturado ? `${noLeidas}+` : noLeidas}
          </span>
        )}
      </button>

      {abierta && (
        <div
          role="dialog"
          aria-label="Novedades"
          className="absolute right-0 z-40 mt-1.5 w-[330px] max-w-[90vw] overflow-hidden
            rounded-md border border-acero-300 bg-white"
        >
          <div className="border-b border-acero-200 bg-acero-50 px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.09em] text-acero-600">
              Novedades de sus órdenes
            </span>
          </div>

          {consulta.isPending ? (
            <p className="px-3 py-3 text-[12.5px] text-acero-500">Cargando…</p>
          ) : consulta.isError ? (
            <p className="px-3 py-3 text-[12.5px] text-acero-500" role="alert">
              No se pudieron cargar las novedades.
            </p>
          ) : datos.length === 0 ? (
            /*
              El vacío dice QUÉ va a aparecer acá, no solo que no hay nada. Sin
              eso, una campana vacía se lee como una que no funciona.
            */
            <p className="px-3 py-3 text-[12.5px] text-acero-500">
              Todavía no hay novedades. Acá van a aparecer los cambios de estado de sus órdenes y
              lo que haya que revisar.
            </p>
          ) : (
            <ul className="max-h-[380px] overflow-y-auto">
              {datos.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => ir(n.enlace)}
                    className={`w-full border-t border-acero-200 px-3 py-2.5 text-left
                      first:border-t-0 hover:bg-acero-50 ${n.noLeida ? 'bg-senal-100/40' : ''}`}
                  >
                    <span className="flex items-start gap-2">
                      {/* Punto solo en lo no leído: distingue de un vistazo sin
                          repetir la palabra «nuevo» en cada fila. */}
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                          n.noLeida ? 'bg-senal-500' : 'bg-transparent'
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12.5px] text-acero-900">{n.titulo}</span>
                        {n.detalle && (
                          <span className="mt-0.5 block text-[11.5px] leading-snug text-acero-600">
                            {n.detalle}
                          </span>
                        )}
                        <span className="mt-0.5 block text-[11px] text-acero-500">
                          {hace(n.creadoEn)}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
